import {
    MdOutlineAddCircleOutline,
    MdOutlineAlarm,
    MdOutlineCheckCircle,
    MdOutlineDeleteOutline,
    MdOutlineEdit,
    MdOutlineEventRepeat,
    MdOutlineHistory,
    MdOutlinePersonAddAlt,
} from 'react-icons/md'

import { describeHistoryValue } from '@/Containers/CrmSection/Components/History'
import { HistoryDiff, HistoryField, HistoryFields, HistoryMuted, HistoryQuote } from '@/Ship/Components/History'
import { formatDateTimeUk } from '@/Ship/Helpers/fieldViews'

import { ACTIVITY_FIELD_LABELS as L } from '../Contracts/ActivityFieldLabels'

import type { ActivityHistoryEventInfo, ActivityHistoryKind } from '../Contracts/ActivityHistoryEvent'
import type { HistoryEntry } from '@/Ship/Components/History'
import type { UserLike } from '@/Ship/Helpers/fieldViews'
import type { ReactNode } from 'react'

/** Подія історії справи (`GET /services/activities/{uuid}/history`, Ship `AbstractHistoryEntity`). */
export interface ActivityHistoryItem {
    uuid?: string
    event: string
    payload?: Record<string, unknown> | null
    changedBy?: UserLike | null
    occurredAt?: string
}

const ICONS: Record<ActivityHistoryKind, ReactNode> = {
    created: <MdOutlineAddCircleOutline />,
    updated: <MdOutlineEdit />,
    assigned: <MdOutlinePersonAddAlt />,
    deadline: <MdOutlineEventRepeat />,
    overdue: <MdOutlineAlarm />,
    completed: <MdOutlineCheckCircle />,
    removed: <MdOutlineDeleteOutline />,
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)

const isBlank = (value: unknown): boolean =>
    value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)

/** Дата з `payload` (ISO 8601) — «07.10.2026, 15:00»; не дата — як є. */
const dateText = (value: unknown): string => {
    const formatted = formatDateTimeUk(value)

    return formatted !== '—' ? formatted : describeHistoryValue(value)
}

/** Власник у `payload.new.owner`: `{kind, uuid, title}`. */
const ownerRow = (owner: unknown): ReactNode => {
    if (!isRecord(owner) || isBlank(owner['title'])) return null

    return (
        <HistoryField key="owner" label={owner['kind'] === 'carrier' ? L.carrier : L.client}>
            {describeHistoryValue(owner['title'])}
        </HistoryField>
    )
}

/** Зміна одного поля `{field, label, old, new}`: було → стало, додано, знято. */
const changeRow = (change: Record<string, unknown>, index: number): ReactNode => {
    const label = typeof change['label'] === 'string' ? change['label'] : String(change['field'] ?? '—')
    const key = `${String(change['field'] ?? label)}-${index}`
    const hasOld = !isBlank(change['old'])
    const hasNew = !isBlank(change['new'])

    if (change['masked'] === true || (!hasOld && !hasNew)) return <HistoryField key={key} label={label} action="changed" />

    if (!hasOld) {
        return (
            <HistoryField key={key} label={label} action="added">
                {describeHistoryValue(change['new'])}
            </HistoryField>
        )
    }

    if (!hasNew) {
        return (
            <HistoryField key={key} label={label} action="removed">
                {describeHistoryValue(change['old'])}
            </HistoryField>
        )
    }

    return (
        <HistoryField key={key} label={label}>
            <HistoryDiff from={describeHistoryValue(change['old'])} to={describeHistoryValue(change['new'])} />
        </HistoryField>
    )
}

/** Подробиці події за її видом (`SPEC-011` §6, колонка `payload`). */
const details = (kind: ActivityHistoryKind, payload: Record<string, unknown>): ReactNode => {
    switch (kind) {
        case 'created': {
            const created = isRecord(payload['new']) ? payload['new'] : {}
            const rows: ReactNode[] = []

            if (!isBlank(created['type'])) {
                rows.push(
                    <HistoryField key="type" label={L.type}>
                        {describeHistoryValue(created['type'])}
                    </HistoryField>
                )
            }

            rows.push(ownerRow(created['owner']))

            if (!isBlank(created['contact'])) {
                rows.push(
                    <HistoryField key="contact" label="Контакт">
                        {describeHistoryValue(created['contact'])}
                    </HistoryField>
                )
            }

            if (!isBlank(created['responsible'])) {
                rows.push(
                    <HistoryField key="responsible" label={L.responsible}>
                        {describeHistoryValue(created['responsible'])}
                    </HistoryField>
                )
            }

            if (!isBlank(created['deadline'])) {
                rows.push(
                    <HistoryField key="deadline" label={L.deadline}>
                        {dateText(created['deadline'])}
                    </HistoryField>
                )
            }

            const filled = rows.filter((row) => row !== null)

            return filled.length > 0 ? <HistoryFields>{filled}</HistoryFields> : null
        }

        case 'updated': {
            const changes = (Array.isArray(payload['changes']) ? payload['changes'] : []).filter(isRecord)

            return changes.length > 0 ? <HistoryFields>{changes.map(changeRow)}</HistoryFields> : null
        }

        case 'assigned':
            return (
                <HistoryFields>
                    <HistoryField label={L.responsible}>
                        <HistoryDiff
                            from={describeHistoryValue(payload['old'])}
                            to={describeHistoryValue(payload['new'])}
                            strike={false}
                        />
                    </HistoryField>
                </HistoryFields>
            )

        case 'deadline':
            return (
                <HistoryFields>
                    <HistoryField label={L.deadline}>
                        <HistoryDiff from={dateText(payload['old'])} to={dateText(payload['new'])} />
                    </HistoryField>
                </HistoryFields>
            )

        case 'overdue':
            return isBlank(payload['deadline']) ? null : (
                <HistoryFields>
                    <HistoryField label={L.deadline}>{dateText(payload['deadline'])}</HistoryField>
                </HistoryFields>
            )

        case 'completed': {
            const result = payload['result']

            return typeof result === 'string' && result.trim() !== '' ? (
                <HistoryFields>
                    <HistoryField label={L.result}>
                        <HistoryQuote>{result}</HistoryQuote>
                    </HistoryField>
                </HistoryFields>
            ) : null
        }

        default:
            return null
    }
}

/**
 * Події історії справи → рядки спільної стрічки `HistoryTimeline` (ADR 041).
 *
 * Невідомий код (бек додав подію, а фронт ще ні) не ховаємо — показуємо його
 * як є, щоб було видно, що мапу треба доповнити. Подія без автора — «Система»
 * (так її підписує сама стрічка для `actor: null`): прострочення фіксує воркер.
 */
export const buildActivityHistoryEntries = (
    items: readonly ActivityHistoryItem[],
    map: Readonly<Record<string, ActivityHistoryEventInfo>>
): HistoryEntry[] =>
    items.map((item, index) => {
        const info = map[item.event]
        const payload = isRecord(item.payload) ? item.payload : {}

        return {
            key: item.uuid ?? `${item.event}-${item.occurredAt ?? ''}-${index}`,
            title: info?.label ?? item.event,
            icon: info ? ICONS[info.kind] : <MdOutlineHistory />,
            tone: info?.tone ?? 'neutral',
            actor: item.changedBy ?? null,
            occurredAt: item.occurredAt,
            details: info ? details(info.kind, payload) : <HistoryMuted>{item.event}</HistoryMuted>,
        }
    })
